import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import Dropdown from 'rc-dropdown';
import { Control, Controller, ControllerRenderProps } from 'react-hook-form';

import HelpMenuDropdown from '@/components/shared/HelpMenuDropDown';
import { IDesiredTerms } from 'types/loan';

import { DesiredTermsItemContainer, DesiredTermsLabel } from './DesiredTerms';

interface ICustomDuration {
  field: ControllerRenderProps<IDesiredTerms>;
  isCustom: boolean;
  setCustom: Dispatch<SetStateAction<boolean>>;
}
const DAYS = ['12', '20', '30', '60'].map((day) => ({ label: `${day} Days`, value: day }));

const durationMenu = [...DAYS, 'Custom'];

const CustomDuration = ({ field, isCustom, setCustom }: ICustomDuration) => {
  const popupParentContainer = useRef<HTMLDivElement>(null);

  const getPopupContainer = popupParentContainer.current ? () => popupParentContainer.current as HTMLElement : () => document.body;

  return (
    <>
      <div className='!bg-transparent my-auto pl-3 flex-grow relative' ref={popupParentContainer}>
        <Dropdown
          animation='slide-up'
          getPopupContainer={getPopupContainer}
          overlay={
            <HelpMenuDropdown
              customStyle='max-h-[220px] overflow-y-auto !shadow-none !border-0  !bg-light-purple [&>li]:!px-4 [&>li]:!pt-[10px] [&>li]:!pb-2 flex flex-col cursor-pointer'
              categoryHandler={(selectedDuration) => {
                const isCustomSelected = selectedDuration === 'Custom';
                field.onChange(isCustomSelected ? 0 : parseInt(selectedDuration, 10));
                setCustom(isCustomSelected);
                if (isCustomSelected) {
                  field.onChange(0);
                }
              }}
              menuItemClassName='!bg-light-purple hover:!bg-white hover:shadow transition rounded !text-primary font-medium'
              menu={durationMenu}
            />
          }
          trigger={['click']}
          overlayClassName='right-0 !top-[35px] -mt-[7px]'
        >
          <div className='max-h-[25px] gap-[6px] border-solid py-[7px] bg-transparent dark:border-none dark:!bg-transparent flex justify-between rounded dark:border items-center cursor-pointer !relative'>
            <span className='text-sm placeholder:text-sm text-coal-300 dark:text-coal-50 font-space-mono ml-auto'>{isCustom ? 'Custom' : `${field.value} Days`}</span>
          </div>
        </Dropdown>
      </div>
    </>
  );
};

interface ICustomFieldDuration {
  control: Control<IDesiredTerms>;
  name: keyof IDesiredTerms;
}

const NON_CLICKABLE_BUTTONS = ['Backspace', 'Shift', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight', 'Delete'];

const CustomFieldDuration = ({ control, name }: ICustomFieldDuration) => {
  const [isCtrlPressed, setCtrlPressed] = useState(false);
  const inputCustom = useRef<HTMLDivElement>(null);

  return (
    <div
      onClick={() => {
        inputCustom.current?.focus();
      }}
      className='bg-[#141514] rounded flex justify-center dark:border-coal-border dark:bg-jet-black h-full items-center gap-2 '
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            if (!inputCustom.current) return;
            if (field && field.value) {
              inputCustom.current.innerText = field.value.toString();
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, []);

          return (
            <div
              ref={inputCustom}
              className='!text-coal-250 dark:!text-white text-sm font-normal flex items-center !bg-transparent text-right focus-visible:outline-none'
              contentEditable
              onKeyDown={(e) => {
                if (e.key === 'Control') {
                  setCtrlPressed(true);
                  return;
                }
                if (NON_CLICKABLE_BUTTONS.includes(e.key) || isCtrlPressed) return;
                const number = +(e.currentTarget.innerText + e.key);
                if (Number.isNaN(number) || number > 100000000) {
                  e.preventDefault();
                }
              }}
              onKeyUp={(e) => {
                if (e.key === 'Control') {
                  setCtrlPressed(false);
                }
              }}
              onInput={(e) => {
                const number = +e.currentTarget.innerText;
                field.onChange(number);
              }}
            />
          );
        }}
      />
      <span className='text-sm'>Days</span>
    </div>
  );
};

interface IDurationPickerProps {
  control: Control<IDesiredTerms>;
  name: keyof IDesiredTerms;
  label: string;
  containerClassName?: string;
  labelClassName?: string;
}

function DurationPicker({ control, name, label, containerClassName, labelClassName }: IDurationPickerProps) {
  const [isSelectedCustom, setSelectedCustom] = useState(false);
  return (
    <div>
      <DesiredTermsItemContainer simplified containerClassName={containerClassName}>
        <DesiredTermsLabel label={label} labelClassName={labelClassName} />
        <Controller name={name} control={control} render={(renderProps) => CustomDuration({ ...renderProps, isCustom: isSelectedCustom, setCustom: setSelectedCustom })} />
      </DesiredTermsItemContainer>
      {isSelectedCustom ? (
        <DesiredTermsItemContainer>
          <CustomFieldDuration name={name} control={control} />
        </DesiredTermsItemContainer>
      ) : null}
    </div>
  );
}

export default DurationPicker;
